import { createSlice } from "@reduxjs/toolkit";

const getInitialTheme = () => {
  if (typeof window !== "undefined" && window.localStorage) {
    const storedPrefs = window.localStorage.getItem("color-theme");
    if (typeof storedPrefs === "string") {
      return storedPrefs;
    }
    const userMedia = window.matchMedia("(prefers-color-scheme: dark)");
    if (userMedia.matches) {
      return "dark";
    }
  }
  return "light";
};

const uiSlice = createSlice({
  name: "ui",
  initialState: {
    theme: getInitialTheme(),
    selectedActivityUid: null,
    // Transient canvas cross-highlight, both directions of the same question
    // ("which activity drives which transition?"). Never persisted, never part
    // of a save: hoveredActivity is {nodeId, label} while an activity row in a
    // state is under the cursor, hoveredEdgeId is the edge under the cursor.
    // Activity labels are the join key because that is what an edge stores in
    // data.activities (see EdgePropertyPanel).
    hoveredActivity: null,
    hoveredEdgeId: null,
    // The element the diagram search box last flew the viewport to, so the
    // canvas can pulse a ring around it — or null. Shape:
    // {nodeId, edgeId, activityUid, token}. `activityUid` is set only when the
    // match was an activity living inside a state, so StateNode can also ring
    // that one row (an INIT activity is its own node and needs no row ring).
    // `token` is a monotonic counter, not decoration: jumping to the SAME
    // match twice (Enter on a single-result search) leaves nodeId/edgeId
    // unchanged, so without it the second jump is a no-op write and the
    // animation never restarts. Cleared on a timer by DiagramSearch; also
    // transient and never saved.
    searchHighlight: null,
    addActivityModal: {
      isVisible: false,
      stateId: null,
    },
    addExistingActivityModal: {
      isVisible: false,
      stateId: null,
    },
    confirmation: {
      isVisible: false,
      message: "",
      type: null,
      id: null,
      secondaryId: null,
      onConfirm: null,
      onSecondaryConfirm: null,
      secondaryConfirmLabel: null,
      data: null,
    },
    toast: {
      isVisible: false,
      message: "",
      type: "success",
    },
    jsonModal: {
      isVisible: false,
      isRaw: false,
    },
    triggerFlowPopup: {
      isVisible: false,
      activityUid: null,
      activityName: "",
    },
    // Dirty tracking uses a Set-of-owners model so independent editors
    // can flag dirtiness without stomping each other. `selectIsDirty`
    // returns true iff `dirtyOwners.length > 0`. Direct dispatchers
    // (~50 callsites across the codebase) keep using `setIsDirty(bool)`,
    // which manipulates a special 'legacy' owner — equivalent behavior
    // for any single caller, but a useDirtyState instance with its own
    // owner ID can no longer be cleared by a sibling's setIsDirty(false).
    dirtyOwners: [],
    activeView: "diagram",
    breadcrumbs: [],
    // Target-mode capability from GET /auth/me: "" (builder) or "consumer".
    // Not persisted — fail-closed on !targetModeResolved until /auth/me answers,
    // so a stale value can't survive an account switch. UI trim only.
    targetMode: "",
    targetModeResolved: false,
  },
  reducers: {
    setTheme: (state, action) => {
      state.theme = action.payload;
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem("color-theme", action.payload);
        document.documentElement.classList.remove("dark", "grey");
        if (action.payload !== "light") {
          document.documentElement.classList.add(action.payload);
        }
      }
    },
    setHoveredActivity: (state, action) => {
      state.hoveredActivity = action.payload;
    },
    setHoveredEdgeId: (state, action) => {
      state.hoveredEdgeId = action.payload;
    },
    setSelectedActivityUid: (state, action) => {
      state.selectedActivityUid = action.payload;
    },
    setSearchHighlight: (state, action) => {
      state.searchHighlight = action.payload;
    },
    openAddActivityModal: (state, action) => {
      state.addActivityModal.isVisible = true;
      state.addActivityModal.stateId = action.payload.stateId;
    },
    closeAddActivityModal: (state) => {
      state.addActivityModal.isVisible = false;
      state.addActivityModal.stateId = null;
    },
    openExistingActivityModal: (state, action) => {
      state.addExistingActivityModal.isVisible = true;
      state.addExistingActivityModal.stateId = action.payload.stateId;
    },
    closeExistingActivityModal: (state) => {
      state.addExistingActivityModal.isVisible = false;
      state.addExistingActivityModal.stateId = null;
    },
    openConfirmation: (state, action) => {
      state.confirmation = {
        isVisible: true,
        message: action.payload.message,
        type: action.payload.type,
        id: action.payload.id || null,
        secondaryId: action.payload.secondaryId || null,
        onConfirm: action.payload.onConfirm || null,
        onSecondaryConfirm: action.payload.onSecondaryConfirm || null,
        secondaryConfirmLabel: action.payload.secondaryConfirmLabel || null,
        confirmLabel: action.payload.confirmLabel || null,
        data: action.payload.data || null,
      };
    },
    closeConfirmation: (state) => {
      state.confirmation.isVisible = false;
    },
    showToast: (state, action) => {
      state.toast.isVisible = true;
      state.toast.message = action.payload.message;
      state.toast.type = action.payload.type || "success";
      // Optional override of Toast's 3000ms default. Callers can pass a
      // longer duration for verbose warnings (e.g. cross-version field-drop
      // notice) that need more reading time.
      state.toast.duration = action.payload.duration;
    },
    hideToast: (state) => {
      state.toast.isVisible = false;
    },
    openJsonModal: (state, action) => {
      state.jsonModal.isVisible = true;
      state.jsonModal.isRaw = action.payload?.isRaw || false;
    },
    closeJsonModal: (state) => {
      state.jsonModal.isVisible = false;
    },
    openTriggerFlowPopup: (state, action) => {
      state.triggerFlowPopup.isVisible = true;
      state.triggerFlowPopup.activityUid = action.payload.activityUid;
      state.triggerFlowPopup.activityName = action.payload.activityName;
    },
    closeTriggerFlowPopup: (state) => {
      state.triggerFlowPopup.isVisible = false;
      state.triggerFlowPopup.activityUid = null;
      state.triggerFlowPopup.activityName = "";
    },
    // Backward-compat: 50+ callsites dispatch this directly. We route
    // them through a single 'legacy' owner so they keep working but no
    // longer wipe out useDirtyState's per-instance owners.
    setIsDirty: (state, action) => {
      const wantDirty = !!action.payload;
      const has = state.dirtyOwners.includes('legacy');
      if (wantDirty && !has) state.dirtyOwners.push('legacy');
      if (!wantDirty && has) state.dirtyOwners = state.dirtyOwners.filter(id => id !== 'legacy');
    },
    addDirtyOwner: (state, action) => {
      const id = action.payload;
      if (id && !state.dirtyOwners.includes(id)) state.dirtyOwners.push(id);
    },
    removeDirtyOwner: (state, action) => {
      const id = action.payload;
      if (id) state.dirtyOwners = state.dirtyOwners.filter(o => o !== id);
    },
    clearAllDirty: (state) => {
      state.dirtyOwners = [];
    },
    setActiveView: (state, action) => {
      state.activeView = action.payload;
    },
    setBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload;
    },
    setTargetMode: (state, action) => {
      state.targetMode = action.payload || "";
      state.targetModeResolved = true;
    },
  },
  extraReducers: (builder) => {
    // Cross-slice reactions. String action types are the standard RTK pattern
    // when the source slice lives in a file that imports this one (circular dep).
    // Grouped by concern for maintainability.

    // Reset all UI state when workflow is reset
    builder.addCase("workflow/resetWorkflow", (state) => {
      state.dirtyOwners = [];
      state.selectedActivityUid = null;
      state.activeView = "diagram";
    });

    // Clear activity selection on any node/form change
    const clearActivitySelection = (state) => { state.selectedActivityUid = null; };
    builder.addCase("workflow/setSelectedNode", clearActivitySelection);
    builder.addCase("workflow/deleteNode", clearActivitySelection);

    // Mark dirty + clear selection on form delete
    builder.addCase("workflow/deleteForm", (state) => {
      state.selectedActivityUid = null;
      if (!state.dirtyOwners.includes('legacy')) state.dirtyOwners.push('legacy');
    });
  },
});

export default uiSlice;
